<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import { realEstateCommissionSupportSortOptions } from '../composables/realEstateCommissionSupportFilterOptions';
import RealEstateCommissionSupportFilterSelect from './RealEstateCommissionSupportFilterSelect.vue';

const props = defineProps({
  filters: { type: Object, required: true },
  catalog: { type: Object, default: () => ({}) },
  loading: { type: Boolean, default: false },
  showAdvanced: { type: Boolean, default: false },
  showOrder: { type: Boolean, default: false },
});

const emit = defineEmits(['apply', 'clear']);
const { t } = useI18n();
const draft = ref({ ...props.filters });
const managerQuery = ref('');

const optionValue = option => option?.id ?? option?.value ?? '';
const optionLabel = option => option?.label ?? option?.name ?? '';
const managers = computed(() => props.catalog.managers || []);
const sortOptions = computed(() => realEstateCommissionSupportSortOptions(t));

const syncManagerQuery = () => {
  const selected = draft.value.filtro_gerente?.[0];
  const selectedValue = selected?.id ?? selected;
  const option = managers.value.find(
    item => String(optionValue(item)) === String(selectedValue ?? '')
  );
  managerQuery.value = option ? optionLabel(option) : '';
};

watch(
  () => props.filters,
  value => {
    draft.value = { ...value };
    syncManagerQuery();
  },
  { deep: true, immediate: true }
);

watch(managers, syncManagerQuery);

const activeCount = computed(() =>
  Object.values(draft.value).reduce((count, value) => {
    if (Array.isArray(value)) return count + value.length;
    return count + (value ? 1 : 0);
  }, 0)
);

const selectManager = () => {
  const query = managerQuery.value.trim().toLocaleLowerCase();
  const option = managers.value.find(
    item => optionLabel(item).toLocaleLowerCase() === query
  );
  draft.value.filtro_gerente = option ? [optionValue(option)] : [];
};

const apply = () => {
  selectManager();
  emit('apply', { ...draft.value });
};

const clear = () => {
  managerQuery.value = '';
  draft.value = {
    filtro_endereco: '',
    filtro_numero: '',
    filtro_gerente: [],
    filtro_responsavel: [],
    filtro_loja: [],
    filtro_tipo_venda: [],
    ordenacao: '',
  };
  emit('clear');
};
</script>

<template>
  <section class="flex w-full min-w-0 gap-[15px]">
    <label
      class="relative block min-w-0 w-[48vw] max-[1400px]:w-[calc(48vw-5px)]"
    >
      <span class="sr-only">
        {{ t('REAL_ESTATE_COMMISSION_SUPPORT.FILTERS.ADDRESS') }}
      </span>
      <span
        class="i-lucide-search absolute left-2.5 top-1/2 size-5 -translate-y-1/2 text-[#01988C]"
        aria-hidden="true"
      />
      <input
        v-model="draft.filtro_endereco"
        list="commission-support-addresses"
        type="search"
        class="!m-0 !h-14 !w-full !rounded-[12px] !border !border-[#E0E7EB] !bg-white !pl-[39px] !pr-4 font-[Roboto] !text-base !leading-[23px] text-[#000000DE] outline-none placeholder:text-[#A7B7BE] focus:border-[#01988C]"
        :placeholder="
          t('REAL_ESTATE_COMMISSION_SUPPORT.FILTERS.SEARCH_ADDRESS')
        "
        :disabled="loading"
        @keyup.enter="apply"
      />
      <datalist id="commission-support-addresses">
        <option
          v-for="address in catalog.addresses || []"
          :key="address.id || address.label"
          :value="address.logradouro || address.label"
        />
      </datalist>
    </label>
    <label
      class="relative block min-w-0 w-[40vw] max-[1400px]:w-[calc(40vw+5px)]"
    >
      <span class="sr-only">
        {{ t('REAL_ESTATE_COMMISSION_SUPPORT.FILTERS.MANAGER') }}
      </span>
      <span
        class="i-lucide-search absolute left-2.5 top-1/2 size-5 -translate-y-1/2 text-[#01988C]"
        aria-hidden="true"
      />
      <input
        v-model="managerQuery"
        list="commission-support-managers"
        type="search"
        class="!m-0 !h-14 !w-full !rounded-[12px] !border !border-[#E0E7EB] !bg-white !pl-[39px] !pr-10 font-[Roboto] !text-base !leading-[23px] text-[#000000DE] outline-none placeholder:text-[#A7B7BE] focus:border-[#01988C]"
        :placeholder="
          t('REAL_ESTATE_COMMISSION_SUPPORT.FILTERS.SEARCH_MANAGER')
        "
        :disabled="loading"
        @change="selectManager"
        @keyup.enter="apply"
      />
      <span
        class="i-lucide-chevron-down absolute right-3 top-1/2 size-4 -translate-y-1/2 text-[#01988C]"
        aria-hidden="true"
      />
      <datalist id="commission-support-managers">
        <option
          v-for="manager in managers"
          :key="optionValue(manager)"
          :value="optionLabel(manager)"
        />
      </datalist>
    </label>
  </section>

  <section
    v-if="showAdvanced || showOrder"
    class="rounded-[10px] border border-[#D5DEE2] bg-white p-4 shadow-lais-card"
  >
    <div v-if="showAdvanced" class="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      <LaisField :label="t('REAL_ESTATE_COMMISSION_SUPPORT.FILTERS.NUMBER')">
        <input
          v-model="draft.filtro_numero"
          type="search"
          class="min-h-10 rounded-lais border border-lais-border px-3 text-sm"
          @keyup.enter="apply"
        />
      </LaisField>
      <RealEstateCommissionSupportFilterSelect
        v-model="draft.filtro_responsavel"
        :label="t('REAL_ESTATE_COMMISSION_SUPPORT.FILTERS.RESPONSIBLE')"
        :options="catalog.responsibles || []"
      />
      <RealEstateCommissionSupportFilterSelect
        v-model="draft.filtro_loja"
        :label="t('REAL_ESTATE_COMMISSION_SUPPORT.FILTERS.STORE')"
        :options="catalog.stores || []"
      />
      <RealEstateCommissionSupportFilterSelect
        v-model="draft.filtro_tipo_venda"
        :label="t('REAL_ESTATE_COMMISSION_SUPPORT.FILTERS.SALE_TYPE')"
        :options="catalog.saleTypes || []"
      />
    </div>
    <div v-if="showOrder" class="max-w-sm">
      <RealEstateCommissionSupportFilterSelect
        v-model="draft.ordenacao"
        :label="t('REAL_ESTATE_COMMISSION_SUPPORT.FILTERS.SORT')"
        :options="sortOptions"
        :multiple="false"
      />
    </div>
    <div
      class="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-lais-border-soft pt-4"
    >
      <span class="mr-auto text-xs text-lais-text-muted">
        {{
          t('REAL_ESTATE_COMMISSION_SUPPORT.FILTERS.ACTIVE', {
            count: activeCount,
          })
        }}
      </span>
      <LaisPrimaryButton variant="ghost" :disabled="loading" @click="clear">
        {{ t('REAL_ESTATE_COMMISSION_SUPPORT.ACTIONS.CLEAR') }}
      </LaisPrimaryButton>
      <LaisPrimaryButton :is-loading="loading" @click="apply">
        {{ t('REAL_ESTATE_COMMISSION_SUPPORT.ACTIONS.APPLY') }}
      </LaisPrimaryButton>
    </div>
  </section>
</template>
